import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Scissors, FileVideo, Upload, Loader2, CheckCircle2, AlertCircle, Download } from 'lucide-react';
import '../App.css';
import './VideoSplitterPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export const VideoSplitterPage = () => {
    const [file, setFile] = useState(null);
    const [duration, setDuration] = useState('30');
    const [status, setStatus] = useState({
        stage: 'idle', // idle, uploading, splitting, complete, error
        message: '',
        progress: 0,
        downloadUrl: null,
        filename: null,
        clipCount: 0,
        error: null,
        jobId: null,
    });
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const pollIntervalRef = useRef(null);

    // Clean up polling on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    // Poll for status when we have a job ID
    useEffect(() => {
        if (status.stage === 'splitting' && status.jobId) {
            pollIntervalRef.current = setInterval(async () => {
                try {
                    const res = await fetch(`${API_BASE}/api/splitter/status/${status.jobId}?user_id=anonymous`);
                    const data = await res.json();

                    if (data.status === 'complete') {
                        clearInterval(pollIntervalRef.current);
                        setStatus({
                            stage: 'complete',
                            message: 'Complete!',
                            progress: 100,
                            downloadUrl: data.download_url,
                            filename: data.filename,
                            clipCount: data.clip_count,
                            error: null,
                            jobId: null,
                        });
                    } else if (data.status === 'error') {
                        clearInterval(pollIntervalRef.current);
                        setStatus(prev => ({
                            ...prev,
                            stage: 'error',
                            error: data.error || 'Split failed',
                            jobId: null,
                        }));
                    } else {
                        // Still processing - update message and progress
                        let progress = 70;
                        if (data.current_clip && data.total_clips) {
                            progress = 70 + (data.current_clip / data.total_clips) * 25;
                        }
                        setStatus(prev => ({
                            ...prev,
                            message: data.message || 'Processing...',
                            progress: Math.min(95, progress),
                        }));
                    }
                } catch (err) {
                    console.error('Status poll error:', err);
                }
            }, 2000);

            return () => {
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                }
            };
        }
    }, [status.stage, status.jobId]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && isValidVideo(droppedFile)) {
            setFile(droppedFile);
            setStatus(prev => ({ ...prev, error: null }));
        } else {
            setStatus(prev => ({ ...prev, error: 'Please select a valid video file (MP4, WebM, MKV, MOV)' }));
        }
    }, []);

    const isValidVideo = (file) => {
        const validTypes = ['video/mp4', 'video/webm', 'video/x-matroska', 'video/quicktime', 'video/x-msvideo'];
        const validExtensions = ['.mp4', '.webm', '.mkv', '.mov', '.avi', '.m4v'];
        const hasValidType = validTypes.includes(file.type);
        const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
        return hasValidType || hasValidExtension;
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && isValidVideo(selectedFile)) {
            setFile(selectedFile);
            setStatus(prev => ({ ...prev, error: null }));
        } else if (selectedFile) {
            setStatus(prev => ({ ...prev, error: 'Please select a valid video file' }));
        }
    };

    const handleSplit = async (e) => {
        e.preventDefault();
        if (!file) return;

        const userId = 'anonymous';

        try {
            // Stage 1: Get signed upload URL from backend
            setStatus({
                stage: 'uploading',
                message: 'Preparing upload...',
                progress: 10,
                downloadUrl: null,
                filename: null,
                clipCount: 0,
                error: null,
                jobId: null,
            });

            const urlRes = await fetch(`${API_BASE}/api/splitter/get-upload-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filename: file.name,
                    user_id: userId,
                }),
            });

            const urlData = await urlRes.json();

            if (!urlData.success) {
                throw new Error(urlData.error || 'Failed to get upload URL');
            }

            setStatus(prev => ({
                ...prev,
                progress: 20,
                message: 'Uploading video to cloud...'
            }));

            // Stage 2: Upload directly to Supabase using signed URL
            const uploadRes = await fetch(urlData.upload_url, {
                method: 'PUT',
                headers: {
                    'Content-Type': urlData.content_type,
                },
                body: file,
            });

            if (!uploadRes.ok) {
                throw new Error(`Upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
            }

            setStatus(prev => ({
                ...prev,
                progress: 60,
                message: 'Upload complete. Starting split...'
            }));

            // Stage 3: Start split job (returns immediately)
            const splitRes = await fetch(`${API_BASE}/api/splitter/split`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    supabase_path: urlData.supabase_path,
                    user_id: userId,
                    duration: parseInt(duration),
                }),
            });

            const splitData = await splitRes.json();

            if (!splitData.success) {
                throw new Error(splitData.error || 'Failed to start split');
            }

            // Start polling for status
            setStatus({
                stage: 'splitting',
                message: 'Splitting video into clips...',
                progress: 70,
                downloadUrl: null,
                filename: null,
                clipCount: 0,
                error: null,
                jobId: splitData.job_id,
            });

        } catch (err) {
            console.error('Split error:', err);
            setStatus(prev => ({
                ...prev,
                stage: 'error',
                error: err.message,
                message: '',
                jobId: null,
            }));
        }
    };

    const handleReset = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
        }
        setFile(null);
        setStatus({
            stage: 'idle',
            message: '',
            progress: 0,
            downloadUrl: null,
            filename: null,
            clipCount: 0,
            error: null,
            jobId: null,
        });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDownload = () => {
        if (status.downloadUrl) {
            window.open(status.downloadUrl, '_blank');
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const isProcessing = status.stage === 'uploading' || status.stage === 'splitting';

    return (
        <div className="video-splitter-page">
            <div className="splitter-header">
                <h1>Video Splitter</h1>
                <p>Split long videos into perfectly timed clips</p>
            </div>

            <div style={{
                maxWidth: '800px',
                width: '100%',
                padding: '40px',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.05) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '20px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
            }}>
                {status.stage === 'idle' && (
                    <form onSubmit={handleSplit} style={{ gap: '24px' }}>
                        {/* File Drop Zone */}
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                padding: '40px',
                                border: `2px dashed ${isDragging ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)'}`,
                                borderRadius: '12px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                background: isDragging ? 'rgba(255,255,255,0.05)' : 'transparent',
                                marginBottom: '24px',
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="video/*,.mp4,.webm,.mkv,.mov,.avi,.m4v"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            {file ? (
                                <div>
                                    <FileVideo size={48} style={{ color: '#fff', marginBottom: '12px' }} />
                                    <p style={{ color: '#fff', fontWeight: 'bold', marginBottom: '4px' }}>{file.name}</p>
                                    <p style={{ color: '#98a0b3', fontSize: '0.9rem' }}>{formatFileSize(file.size)}</p>
                                </div>
                            ) : (
                                <div>
                                    <Upload size={48} style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }} />
                                    <p style={{ color: '#fff', marginBottom: '4px' }}>Drop video here or click to browse</p>
                                    <p style={{ color: '#98a0b3', fontSize: '0.85rem' }}>Supports MP4, WebM, MKV, MOV (max 500MB)</p>
                                </div>
                            )}
                        </div>

                        {/* Duration Selection */}
                        <div>
                            <label style={{ display: 'block', marginBottom: '12px', color: '#c3cfe0', fontWeight: 'bold' }}>
                                <Scissors size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'text-bottom' }} />
                                Clip Duration
                            </label>
                            <div style={{ display: 'flex', gap: '20px' }}>
                                <label style={{
                                    flex: 1,
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    padding: '20px',
                                    background: duration === '30' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                                    border: duration === '30' ? '2px solid rgba(255,255,255,0.4)' : '2px solid rgba(255,255,255,0.1)',
                                    borderRadius: '12px',
                                    transition: 'all 0.2s ease'
                                }}>
                                    <input
                                        type="radio"
                                        name="duration"
                                        value="30"
                                        checked={duration === '30'}
                                        onChange={(e) => setDuration(e.target.value)}
                                        style={{ display: 'none' }}
                                    />
                                    <span style={{ fontSize: '2rem', fontWeight: 'bold', display: 'block' }}>30s</span>
                                    <span style={{ color: '#c3cfe0' }}>Short Clips</span>
                                </label>
                                <label style={{
                                    flex: 1,
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    padding: '20px',
                                    background: duration === '60' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                                    border: duration === '60' ? '2px solid rgba(255,255,255,0.4)' : '2px solid rgba(255,255,255,0.1)',
                                    borderRadius: '12px',
                                    transition: 'all 0.2s ease'
                                }}>
                                    <input
                                        type="radio"
                                        name="duration"
                                        value="60"
                                        checked={duration === '60'}
                                        onChange={(e) => setDuration(e.target.value)}
                                        style={{ display: 'none' }}
                                    />
                                    <span style={{ fontSize: '2rem', fontWeight: 'bold', display: 'block' }}>60s</span>
                                    <span style={{ color: '#c3cfe0' }}>Long Clips</span>
                                </label>
                            </div>
                        </div>

                        {status.error && (
                            <div className="error" style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                                <AlertCircle size={20} />
                                {status.error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={!file}
                            style={{
                                width: '100%',
                                padding: '16px 24px',
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                marginTop: '20px',
                                background: !file ? 'rgba(255,255,255,0.3)' : '#ffffff',
                                color: '#000',
                                border: '1px solid #ffffff',
                                borderRadius: '12px',
                                cursor: !file ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em'
                            }}
                        >
                            Split Video
                        </button>
                    </form>
                )}

                {isProcessing && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <Loader2 size={48} className="spin" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '20px' }} />
                        <h2 style={{ marginBottom: '10px' }}>
                            {status.stage === 'uploading' ? 'Uploading Video...' : 'Splitting Video...'}
                        </h2>
                        <p style={{ color: '#c3cfe0', marginBottom: '30px' }}>{status.message}</p>

                        <div style={{
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '10px',
                            height: '10px',
                            overflow: 'hidden',
                            marginBottom: '10px'
                        }}>
                            <div style={{
                                width: `${status.progress}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #fff, #e5e5e5)',
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                        <p style={{ fontSize: '0.9rem', color: '#98a0b3' }}>
                            {status.stage === 'uploading' ? 'Uploading to cloud...' : 'This may take a few minutes for large files'}
                        </p>
                    </div>
                )}

                {status.stage === 'complete' && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🎉</div>
                        <h2 style={{ marginBottom: '10px', color: '#fff' }}>Splitting Complete!</h2>
                        <p style={{ color: '#c3cfe0', marginBottom: '30px' }}>
                            Successfully created {status.clipCount} clips.
                        </p>

                        <div style={{
                            background: 'rgba(0,0,0,0.3)',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px'
                        }}>
                            <CheckCircle2 size={24} color="#4ade80" />
                            <span style={{ color: '#fff' }}>{status.filename}</span>
                        </div>

                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                            <button
                                onClick={handleDownload}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '16px 32px',
                                    fontSize: '1.1rem',
                                    fontWeight: '700',
                                    background: '#ffffff',
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                }}
                            >
                                <Download size={20} />
                                Download ZIP
                            </button>
                            <button
                                onClick={handleReset}
                                style={{
                                    padding: '16px 32px',
                                    fontSize: '1.1rem',
                                    fontWeight: '500',
                                    background: 'rgba(255,255,255,0.1)',
                                    color: '#fff',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                }}
                            >
                                Split Another
                            </button>
                        </div>
                    </div>
                )}

                {status.stage === 'error' && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: '20px' }} />
                        <h2 style={{ marginBottom: '10px', color: '#fff' }}>Something Went Wrong</h2>
                        <p style={{ color: '#ef4444', marginBottom: '30px' }}>
                            {status.error}
                        </p>
                        <button
                            onClick={handleReset}
                            style={{
                                padding: '16px 32px',
                                fontSize: '1.1rem',
                                fontWeight: '500',
                                background: 'rgba(255,255,255,0.1)',
                                color: '#fff',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '12px',
                                cursor: 'pointer',
                            }}
                        >
                            Try Again
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
